'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';

const states = [
  { name: 'All States', slug: 'all', code: 'ALL', full: 'All States' },
  { name: 'NSW', slug: 'new-south-wales', code: 'NSW', full: 'New South Wales' },
  { name: 'VIC', slug: 'victoria', code: 'VIC', full: 'Victoria' },
  { name: 'QLD', slug: 'queensland', code: 'QLD', full: 'Queensland' },
  { name: 'WA', slug: 'western-australia', code: 'WA', full: 'Western Australia' },
  { name: 'SA', slug: 'south-australia', code: 'SA', full: 'South Australia' },
  { name: 'TAS', slug: 'tasmania', code: 'TAS', full: 'Tasmania' },
  { name: 'NT', slug: 'northern-territory', code: 'NT', full: 'Northern Territory' },
  { name: 'ACT', slug: 'australian-capital-territory', code: 'ACT', full: 'ACT' },
];

const featureGroups = [
  {
    label: 'Environment',
    tags: ['Beach', 'Mountains', 'Forest', 'Lake', 'River', 'Desert', 'Outback', 'National Park', 'Island', 'Rainforest', 'Coastal', 'Wetlands'],
  },
  {
    label: 'Activities',
    tags: ['Hiking', 'Swimming', 'Fishing', 'Kayaking', 'Surfing', 'Bird Watching', 'Wildlife', 'Stargazing', 'Cycling', 'Photography'],
  },
  {
    label: 'Facilities',
    tags: ['Pet Friendly', 'Powered Sites', 'Toilets', 'Showers', 'Drinking Water', 'BBQ', 'Fire Pits', 'WiFi'],
  },
];

interface RegionFiltersProps {
  onFilterChange?: (f: { state: string; features: string[]; sortBy: string }) => void;
  initialState?: string;
  initialFeatures?: string[];
  initialSortBy?: string;
}

const RegionFilters: React.FC<RegionFiltersProps> = ({
  initialState = 'all',
  initialFeatures = [],
  initialSortBy = 'popular',
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [state, setState] = useState(initialState);
  const [features, setFeatures] = useState<string[]>(initialFeatures);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [showFeatures, setShowFeatures] = useState(false);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  useEffect(() => {
    setState(searchParams?.get('state') || initialState);
    setFeatures(searchParams?.get('features')?.split(',').filter(Boolean) || initialFeatures);
    setSortBy(searchParams?.get('sortBy') || initialSortBy);
  }, [searchParams]);

  const push = (ns: string, nf: string[], nb: string) => {
    const p = new URLSearchParams();
    if (ns !== 'all') p.set('state', ns);
    if (nf.length) p.set('features', nf.join(','));
    if (nb !== 'popular') p.set('sortBy', nb);
    router.push((pathname || '/apps/campsite-explorer/regions') + (p.toString() ? `?${p}` : ''), { scroll: false });
  };

  const handleState = (slug: string) => { setState(slug); push(slug, features, sortBy); };
  const toggleFeature = (tag: string) => {
    const nf = features.includes(tag) ? features.filter(f => f !== tag) : [...features, tag];
    setFeatures(nf); push(state, nf, sortBy);
  };
  const handleSort = (v: string) => { setSortBy(v); push(state, features, v); };
  const clearAll = () => {
    setState('all'); setFeatures([]); setSortBy('popular'); setShowFeatures(false);
    router.push(pathname || '/apps/campsite-explorer/regions', { scroll: false });
  };

  const hasFilters = state !== 'all' || features.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(56,114,229,0.1)] border border-blue-50 overflow-hidden">

      {/* ── Row 1: State pills ── */}
      <div className="px-5 pt-4 pb-3 border-b border-blue-50">
        <div className="flex items-center gap-2 overflow-x-auto pb-0.5" role="radiogroup" aria-label="Filter by state" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap shrink-0 pr-1" id="state-filter-label">State</span>
          {states.map(s => (
            <button
              key={s.code}
              role="radio"
              aria-checked={state === s.slug}
              onClick={() => handleState(s.slug)}
              title={s.full}
              className="whitespace-nowrap shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200"
              style={
                state === s.slug
                  ? { background: 'linear-gradient(135deg, #0066CC 0%, #0052A3 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(0,102,204,0.25)' }
                  : { background: '#f1f5f9', color: '#475569' }
              }
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {/* ── Row 2: Feature toggle + active chips + sort ── */}
      <div className="px-5 py-3 flex flex-wrap items-center gap-2">

        {/* Features button */}
        <button
          onClick={() => setShowFeatures(!showFeatures)}
          aria-expanded={showFeatures}
          aria-controls="feature-filter-panel"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition-all duration-200"
          style={
            features.length > 0 || showFeatures
              ? { borderColor: '#0066CC', color: '#0066CC', background: '#eff6ff' }
              : { borderColor: '#e2e8f0', color: '#64748b' }
          }
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Features
          {features.length > 0 && (
            <span className="text-white text-[10px] font-extrabold w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#0066CC' }}>
              {features.length}
            </span>
          )}
          <svg className={`w-3 h-3 transition-transform duration-200 ${showFeatures ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Active feature chips */}
        {features.map(f => (
          <span key={f} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: '#dbeafe', color: '#1e40af' }}>
            {f}
            <button
              onClick={() => toggleFeature(f)}
              className="w-3.5 h-3.5 rounded-full flex items-center justify-center transition-colors hover:bg-blue-300/50"
              aria-label={`Remove ${f}`}
            >
              <svg className="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}

        {hasFilters && (
          <button onClick={clearAll} className="text-xs text-slate-400 hover:text-slate-700 underline underline-offset-2 transition-colors">
            Clear all
          </button>
        )}

        {/* Sort */}
        <div className="ml-auto">
          <label htmlFor="region-sort" className="sr-only">Sort regions by</label>
          <select
            id="region-sort"
            value={sortBy}
            onChange={e => handleSort(e.target.value)}
            aria-label="Sort regions"
            className="text-sm border border-slate-200 rounded-xl px-3 py-1.5 text-slate-600 bg-white cursor-pointer font-medium focus:outline-none focus:ring-2"
            style={{ focusRingColor: '#0066CC' } as React.CSSProperties}
          >
            <option value="popular">Most Popular</option>
            <option value="a-z">A–Z</option>
            <option value="campsites">Most Campsites</option>
          </select>
        </div>
      </div>

      {/* ── Expandable feature panel ── */}
      {showFeatures && (
        <div id="feature-filter-panel" className="border-t border-blue-50 px-5 py-4" role="group" aria-label="Filter by features">
          {/* Group tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveGroup(null)}
              className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
              style={activeGroup === null ? { background: '#0066CC', color: '#fff' } : { background: '#f1f5f9', color: '#64748b' }}
            >
              All
            </button>
            {featureGroups.map(g => (
              <button
                key={g.label}
                onClick={() => setActiveGroup(activeGroup === g.label ? null : g.label)}
                className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                style={activeGroup === g.label ? { background: '#0066CC', color: '#fff' } : { background: '#f1f5f9', color: '#64748b' }}
              >
                {g.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {featureGroups
              .filter(g => activeGroup === null || g.label === activeGroup)
              .flatMap(g => g.tags)
              .map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleFeature(tag)}
                  className="px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-150"
                  style={
                    features.includes(tag)
                      ? { background: 'linear-gradient(135deg, #0066CC, #3872E5)', color: '#fff', boxShadow: '0 2px 6px rgba(0,102,204,0.2)' }
                      : { background: '#f1f5f9', color: '#475569' }
                  }
                >
                  {tag}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionFilters;
